import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { isInterceptedUrl } from './intercepted-urls';
import { APP_CONFIG } from '../../config/interfaces/iapp-settings.interface';
import { ACT_AS_HEADER, ImpersonationContextService } from '../impersonation/impersonation-context';

// Re-exported from its home beside the context, which is where the non-Angular sender reads it.
export { ACT_AS_HEADER };

/**
 * Sends the id of the person being impersonated.
 *
 * The previous version sent `X-Impersonate-UserID` carrying `originalAdminUser.userID` — the
 * ADMIN's own id, not the target's — so the server was told the one thing it already knew and never
 * the one thing it needed. That is why no server has ever honoured that header.
 *
 * The id comes from the impersonation context rather than from the account object. They would
 * usually agree, but only one of them is the thing the entry link established: an account object can
 * be reloaded, replaced or restored, and each time it is, a target read from it is a guess.
 */
export const impersonationInterceptor: HttpInterceptorFn = (
	req,
	next,
	impersonationContext = inject(ImpersonationContextService),
	appSettings = inject(APP_CONFIG, { optional: true })
) => {
	const actAsUserID = impersonationContext.targetUserID();

	// Same rule as the bearer token: the identity we are acting as is a credential, and it travels
	// exactly where the token travels and nowhere else.
	if (actAsUserID === null || !isInterceptedUrl(req.url, appSettings)) {
		return next(req);
	}

	return next(
		req.clone({
			setHeaders: {
				[ACT_AS_HEADER]: actAsUserID.toString()
			}
		})
	);
};
